import { FC, useEffect, useRef, useState } from 'react';
import { TextInput } from '@highmobility/console-ui-components';

import { ClearanceStatus } from '@/types';
import { DateRangeFilter } from '@/components/molecules/DateRangeFilter';
import { StatusBadge } from '@/components/atoms/StatusBadge';

export interface VehicleClearanceFilters {
  projectIds: string[];
  vins: string[];
  statuses: ClearanceStatus[];
  dateRange: { from: Date | null; to: Date | null };
  oems: number[];
}

export interface VehicleClearanceFilterBarProps {
  currentProjectId: string;
  onSearch: (filters: VehicleClearanceFilters) => void;
  onReset: () => void;
  projects: { id: string; name: string }[];
  availableOems: { ids: number[]; name: string }[];
}

export const VehicleClearanceFilterBar: FC<VehicleClearanceFilterBarProps> = ({
  currentProjectId,
  onSearch,
  onReset,
  projects,
  availableOems,
}) => {
  const defaultFilters: VehicleClearanceFilters = {
    projectIds: [currentProjectId],
    vins: [],
    statuses: [],
    dateRange: { from: null, to: null },
    oems: [],
  };

  const [filters, setFilters] = useState<VehicleClearanceFilters>(defaultFilters);
  const [resetKey, setResetKey] = useState(0);
  const [localVins, setLocalVins] = useState<string[]>([]);

  const [isProjectDropdownOpen, setIsProjectDropdownOpen] = useState(false);
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const [isOemDropdownOpen, setIsOemDropdownOpen] = useState(false);

  const projectRef = useRef<HTMLDivElement>(null);
  const statusRef = useRef<HTMLDivElement>(null);
  const oemRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (projectRef.current && !projectRef.current.contains(target)) {
        setIsProjectDropdownOpen(false);
      }
      if (statusRef.current && !statusRef.current.contains(target)) {
        setIsStatusDropdownOpen(false);
      }
      if (oemRef.current && !oemRef.current.contains(target)) {
        setIsOemDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = () => {
    onSearch({
      ...filters,
      vins: localVins,
    });
  };

  const handleReset = () => {
    setFilters(defaultFilters);
    setLocalVins([]);
    setResetKey((prev) => prev + 1);
    onReset();
  };

  const toggleArrayItem = <K extends 'projectIds' | 'statuses'>(key: K, item: string) => {
    setFilters((prev) => {
      const array = prev[key] as string[];
      const isSelected = array.includes(item);
      return {
        ...prev,
        [key]: isSelected ? array.filter((i) => i !== item) : [...array, item],
      };
    });
  };

  const toggleOemGroup = (groupIds: number[]) => {
    setFilters((prev) => {
      const isSelected = groupIds.some((id) => prev.oems.includes(id));
      return {
        ...prev,
        oems: isSelected
          ? prev.oems.filter((id) => !groupIds.includes(id))
          : [...new Set([...prev.oems, ...groupIds])],
      };
    });
  };

  const selectedOemGroups = availableOems.filter((group) =>
    group.ids.some((id) => filters.oems.includes(id))
  );

  const projectButtonText =
    filters.projectIds.length === 0
      ? 'All Projects'
      : filters.projectIds.length === 1
        ? projects.find((p) => p.id === filters.projectIds[0])?.name || '1 Selected'
        : `${filters.projectIds.length} Projects Selected`;

  const oemButtonText =
    selectedOemGroups.length === 0
      ? 'All OEMs'
      : selectedOemGroups.length === 1
        ? selectedOemGroups[0].name
        : `${selectedOemGroups.length} OEMs Selected`;

  const AVAILABLE_STATUSES: ClearanceStatus[] = [
    'Approved',
    'Pending',
    'Rejected',
    'Revoked',
    'Canceled',
  ];

  return (
    <div className="bg-dark-green-lighter rounded-lg border border-black-20 p-4 shadow-sm">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-6">
        <div className="relative flex flex-col gap-1" ref={projectRef}>
          <label className="text-xs font-medium text-white-80">Projects</label>
          <button
            type="button"
            onClick={() => setIsProjectDropdownOpen(!isProjectDropdownOpen)}
            className="bg-dark-green flex h-9 w-full items-center justify-between truncate rounded border border-black-20 px-3 py-1 text-left text-sm text-white focus:border-white-40 focus:outline-none"
          >
            <span className="truncate pr-2">{projectButtonText}</span>
            <span className="text-[10px] text-white-40">▼</span>
          </button>

          {isProjectDropdownOpen && (
            <div className="absolute left-0 top-[60px] z-50 max-h-60 w-full overflow-y-auto rounded border border-gray-200 bg-dark-green-950 text-white-80 shadow-lg">
              {projects.map((project) => (
                <label
                  key={project.id}
                  className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm text-white transition-colors hover:bg-white-8"
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4 cursor-pointer rounded border-gray-300 accent-blue-900"
                    checked={filters.projectIds.includes(project.id)}
                    onChange={() => toggleArrayItem('projectIds', project.id)}
                  />
                  <span className="truncate">{project.name}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-white-80">VINs</label>
          <TextInput
            key={`vin-input-${resetKey}`}
            name="vins"
            withTags
            textarea
            placeholder="Type & press space"
            className="!bg-dark-green max-h-20 overflow-y-auto !border-black-20 text-sm text-white focus:!border-white-40"
            onChange={(val: any) => {
              const newVins = Array.isArray(val) ? val : val?.target?.value || [];
              setLocalVins(newVins);
            }}
          />
        </div>

        <div className="relative flex flex-col gap-1" ref={statusRef}>
          <label className="text-xs font-medium text-white-80">Status</label>
          <button
            type="button"
            onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
            className="bg-dark-green flex h-9 w-full items-center justify-between truncate rounded border border-black-20 px-3 py-1 text-left text-sm text-white focus:border-white-40 focus:outline-none"
          >
            <div className="flex items-center gap-1 overflow-hidden pr-2">
              {filters.statuses.length === 0 ? (
                <span>All Statuses</span>
              ) : (
                filters.statuses.map((s) => <StatusBadge key={s} status={s} />)
              )}
            </div>
            <span className="text-[10px] text-white-40">▼</span>
          </button>

          {isStatusDropdownOpen && (
            <div className="absolute left-0 top-[60px] z-50 max-h-60 w-full overflow-y-auto rounded border border-gray-200 bg-dark-green-950 shadow-lg">
              {AVAILABLE_STATUSES.map((status) => (
                <label
                  key={status}
                  className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm text-white transition-colors hover:bg-white-8"
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4 cursor-pointer rounded border-gray-300 accent-blue-900"
                    checked={filters.statuses.includes(status)}
                    onChange={() => toggleArrayItem('statuses', status)}
                  />
                  <StatusBadge status={status} />
                </label>
              ))}
            </div>
          )}
        </div>

        <DateRangeFilter
          value={filters.dateRange}
          onChange={(dateRange) => setFilters({ ...filters, dateRange })}
        />

        <div className="relative flex flex-col gap-1" ref={oemRef}>
          <label className="text-xs font-medium text-white-80">OEM</label>
          <button
            type="button"
            onClick={() => setIsOemDropdownOpen(!isOemDropdownOpen)}
            className="bg-dark-green mb-2 flex h-9 w-full items-center justify-between truncate rounded border border-black-20 px-3 py-1 text-left text-sm text-white focus:border-white-40 focus:outline-none"
          >
            <span className="truncate pr-2">{oemButtonText}</span>
            <span className="text-[10px] text-white-40">▼</span>
          </button>

          {isOemDropdownOpen && (
            <div className="absolute left-0 top-[60px] z-50 max-h-60 w-full overflow-y-auto rounded border border-gray-200 bg-dark-green-950 shadow-lg">
              {availableOems.map((oem) => (
                <label
                  key={oem.name}
                  className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm text-white transition-colors hover:bg-white-8"
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4 cursor-pointer rounded border-gray-300 accent-blue-900"
                    checked={oem.ids.some((id) => filters.oems.includes(id))}
                    onChange={() => toggleOemGroup(oem.ids)}
                  />
                  <span className="truncate">{oem.name}</span>
                </label>
              ))}
            </div>
          )}

          <div className="mt-auto flex items-center gap-2">
            <button
              onClick={handleSearch}
              className="flex-1 rounded bg-white px-3 py-1.5 text-sm font-semibold text-[hsl(var(--color-dark-green-900))] transition-opacity hover:opacity-90"
            >
              Search
            </button>
            <button
              onClick={handleReset}
              className="flex-1 rounded border border-black-20 bg-transparent px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-white/5"
            >
              Reset
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
